import { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { AppShell } from '../components/AppShell';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { AssessmentsPage } from '../features/assessments/AssessmentsPage';
import { LaunchesPage } from '../features/launches/LaunchesPage';
import { SheetsPage } from '../features/sheets/SheetsPage';
import { CameraOmrPage } from '../features/omr/CameraOmrPage';
import { StudentsPage } from '../features/registry/StudentsPage';
import { ClassesPage } from '../features/registry/ClassesPage';
import { MatrixPage } from '../features/matrix/MatrixPage';
import { ReportsPage } from '../features/reports/ReportsPage';
import { WritingPage } from '../features/writing/WritingPage';
import { AnalysisPage } from '../features/analysis/AnalysisPage';
import { InterventionsPage } from '../features/interventions/InterventionsPage';
import { EducationPage } from '../features/education/EducationPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { LoginPage } from '../features/auth/LoginPage';
import { hasSupabaseConfig, supabase } from '../lib/supabase';
import { loadProfile, signOutRadar } from '../services/auth';
import { loadAvailableSchools } from '../services/schools';
import type { RadarProfile, SchoolOption } from '../types/app';

function LoadingScreen() {
  return <div className="boot-screen"><img src="/assets/radar-saeb-logo.svg" alt="Radar SAEB"/><span>Carregando ambiente institucional...</span></div>;
}

export function App(){
  const [user,setUser]=useState<User|null>(null);
  const [profile,setProfile]=useState<RadarProfile|null>(null);
  const [schools,setSchools]=useState<SchoolOption[]>([]);
  const [active,setActive]=useState('home');
  const [assessmentCreateRequest,setAssessmentCreateRequest]=useState(0);
  const [sheetAssessmentId,setSheetAssessmentId]=useState('');
  const [omrAssessmentId,setOmrAssessmentId]=useState('');
  const [schoolId,setSchoolId]=useState('');
  const [booting,setBooting]=useState(hasSupabaseConfig);
  const [fatalError,setFatalError]=useState('');
  const [demo,setDemo]=useState(false);

  async function hydrate(nextUser: User | null) {
    setUser(nextUser);
    setFatalError('');
    if (!nextUser) {
      setProfile(null); setSchools([]); setSchoolId(''); setBooting(false); return;
    }
    setBooting(true);
    try {
      const nextProfile = await loadProfile(nextUser);
      const nextSchools = await loadAvailableSchools(nextProfile);
      setProfile(nextProfile);
      setSchools(nextSchools);

      if (!nextSchools.length) throw new Error('Nenhuma escola ativa está disponível para este perfil.');
      const saved = localStorage.getItem('radar-saeb-school');
      const preferred = nextProfile.role === 'admin' && saved && nextSchools.some(s=>s.id===saved)
        ? saved
        : nextProfile.school_id && nextSchools.some(s=>s.id===nextProfile.school_id)
          ? nextProfile.school_id
          : nextSchools[0].id;
      setSchoolId(preferred);
    } catch (err) {
      console.error(err);
      setFatalError(err instanceof Error ? err.message : 'Não foi possível carregar o perfil do usuário.');
      await supabase?.auth.signOut();
      setProfile(null); setSchools([]); setSchoolId('');
    } finally {
      setBooting(false);
    }
  }

  useEffect(()=>{
    if (!supabase) { setBooting(false); return; }
    let mounted = true;
    supabase.auth.getSession().then(({data})=>{ if(mounted) void hydrate(data.session?.user || null); });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session)=>{
      if(mounted) void hydrate(session?.user || null);
    });
    return ()=>{ mounted=false; listener.subscription.unsubscribe(); };
  },[]);

  function changeSchool(id:string){
    setSchoolId(id);
    if(profile?.role==='admin') localStorage.setItem('radar-saeb-school',id);
  }

  async function logout(){
    setActive('home');
    setDemo(false);
    await signOutRadar();
  }

  if(booting)return <LoadingScreen/>;
  if(!hasSupabaseConfig && !demo)return <LoginPage onDemoLogin={()=>setDemo(true)}/>;
  if(hasSupabaseConfig && (!user || !profile))return <><LoginPage/>{fatalError && <div className="fatal-toast">{fatalError}</div>}</>;

  const effectiveProfile:RadarProfile = profile || {id:'demo',name:'Administrador',username:'demo',role:'admin',school_id:null,active:true,must_change_password:false};
  const effectiveSchools = schools.length ? schools : [
    {id:'8c56660d-cbe1-4764-9bbc-d564a61a630c',name:'E. M. General Nelson Custódio de Oliveira',short_name:'General Nelson',active:true},
    {id:'1ef6e6ac-32dd-4599-a9b3-f4d698531b19',name:'E. M. Olyntho Mancini',short_name:'Olyntho Mancini',active:true},
  ];
  const effectiveSchoolId = schoolId || effectiveSchools[0].id;

  return <AppShell active={active} onNavigate={setActive} onLogout={logout} profile={effectiveProfile} schools={effectiveSchools} selectedSchoolId={effectiveSchoolId} onSchoolChange={changeSchool}>
    {active==='home'?<DashboardPage schoolId={effectiveSchoolId} onNewAssessment={()=>{setActive('assessments');setAssessmentCreateRequest(n=>n+1)}}/>:active==='assessments'?<AssessmentsPage schoolId={effectiveSchoolId} profile={effectiveProfile} createRequest={assessmentCreateRequest}/>:active==='launches'?<LaunchesPage schoolId={effectiveSchoolId} profile={effectiveProfile} onOpenSheets={(id)=>{setSheetAssessmentId(id);setActive('sheets')}} onOpenOmr={(id)=>{setOmrAssessmentId(id);setActive('omr')}}/>:active==='sheets'?<SheetsPage schoolId={effectiveSchoolId} profile={effectiveProfile} initialAssessmentId={sheetAssessmentId}/>:active==='omr'?<CameraOmrPage schoolId={effectiveSchoolId} profile={effectiveProfile} initialAssessmentId={omrAssessmentId}/>:active==='students'?<StudentsPage schoolId={effectiveSchoolId} profile={effectiveProfile}/>:active==='classes'?<ClassesPage schoolId={effectiveSchoolId}/>:active==='matrix'?<MatrixPage schoolId={effectiveSchoolId} profile={effectiveProfile}/>:active==='reports'?<ReportsPage schoolId={effectiveSchoolId} school={effectiveSchools.find(s=>s.id===effectiveSchoolId)} profile={effectiveProfile}/>:active==='writing'?<WritingPage schoolId={effectiveSchoolId} profile={effectiveProfile}/>:active==='analysis'?<AnalysisPage schoolId={effectiveSchoolId} profile={effectiveProfile}/>:active==='interventions'?<InterventionsPage schoolId={effectiveSchoolId} profile={effectiveProfile}/>:active==='education'?<EducationPage schoolId={effectiveSchoolId} profile={effectiveProfile} onOpenAnalysis={()=>setActive('analysis')} onOpenInterventions={()=>setActive('interventions')}/>:active==='settings'?<SettingsPage profile={effectiveProfile} school={effectiveSchools.find(s=>s.id===effectiveSchoolId)} schools={effectiveSchools} selectedSchoolId={effectiveSchoolId} onClearSavedSchool={()=>localStorage.removeItem('radar-saeb-school')}/>:<section style={{background:'#fff',padding:28,borderRadius:18,border:'1px solid #e6edf5'}}><h2 style={{marginTop:0}}>Módulo em migração</h2><p style={{color:'#697789'}}>O módulo <b>{active}</b> será migrado preservando a lógica do Radar atual.</p></section>}
  </AppShell>;
}
